import React from 'react';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';

/**
 * Shows a newly created or reset password. Once.
 *
 * WHY THIS IS SHOUTY
 * ------------------
 * The password is hashed the moment it is saved and is never stored in plain
 * text anywhere. There is no screen, no export and no support process that can
 * recover it. If the administrator navigates away without copying it, the only
 * remedy is another reset -- which is survivable, but only if they understand
 * that BEFORE they close the page rather than after.
 *
 * So the banner is large, it does not auto-dismiss, and it says plainly that
 * this is the only time the password will be shown. A toast that fades after
 * four seconds would be the wrong component for a value that cannot be
 * retrieved.
 */
export const PasswordOnce = ({
    password,
    login,
    onDone,
}: {
    password: string;
    login?: string;
    onDone?: () => void;
}) => {
    if (!password) return null;

    const copy = () => {
        try {
            navigator.clipboard.writeText(password);
        } catch {
            // Clipboard access can be refused. The password is on screen and
            // selectable, so failing to copy is an inconvenience, not a loss.
        }
    };

    return (
        <Alert severity="warning" sx={{ mb: 2, width: '100%' }}>
            <AlertTitle>Write this password down now</AlertTitle>

            <Typography variant="body2" sx={{ mb: 1 }}>
                This is the only time it will ever be shown. It is not stored anywhere in a form
                that can be read back &mdash; if it is lost, the only way forward is to reset it
                again.
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                {login && (
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {login}
                    </Typography>
                )}
                <Typography
                    variant="h6"
                    sx={{ fontFamily: 'monospace', letterSpacing: 1, userSelect: 'all' }}
                >
                    {password}
                </Typography>
                <Button size="small" variant="outlined" onClick={copy}>
                    Copy
                </Button>
                {onDone && (
                    <Button size="small" onClick={onDone}>
                        I have written it down
                    </Button>
                )}
            </Box>

            <Typography variant="caption" color="text.secondary">
                They will be asked to change it when they first sign in.
            </Typography>
        </Alert>
    );
};
